<div *ngIf="loading" class="flex items-center justify-center h-64">
  <mat-spinner diameter="50"></mat-spinner>
</div>
<div *ngIf="!isLoginPage && !loading" class="h-screen flex flex-col">
  <!-- Top Toolbar -->
  <mat-toolbar color="accent" class="flex justify-between items-center">
    <div class="flex items-center gap-2">
      <!-- Burger menu button -->
      <button mat-icon-button *ngIf="!hideSidebar" (click)="sidenav.toggle()" class="md:hidden">
        <mat-icon>menu</mat-icon>
      </button>

      <a routerLink="/" class="text-3xl text-primary_color">
        <img [src]="logoDarkUrl" class="h-16 w-auto" alt="Logo"
             (error)="onLogoError($event)" />
      </a>
    </div>

    <span class="flex items-center gap-2">
      <!-- Language Menu -->
      <button mat-icon-button [matMenuTriggerFor]="langMenu">
        <mat-icon>language</mat-icon>
      </button>
      <mat-menu #langMenu="matMenu">
        <button mat-menu-item (click)="switchLanguage('de')">Deutsch</button>
        <button mat-menu-item (click)="switchLanguage('en')">English</button>
      </mat-menu>

      <!-- User Dropdown -->
      <button mat-icon-button [matMenuTriggerFor]="userMenu">
        <mat-icon>account_circle</mat-icon>
      </button>
      <mat-menu #userMenu="matMenu" class="user-menu-wide">
        <button mat-menu-item disabled *ngIf="authService.user$ | async as user">
          <mat-icon>person</mat-icon>
          <span>{{ user.name || user.email }}</span>
        </button>
        <!-- DOCU-812: API-Key-Verwaltung lebt jetzt als Karte auf /organisation -->
        <a *ngIf="userInfoService.isAdminUser()" routerLink="/organisation" mat-menu-item>
          <mat-icon>vpn_key</mat-icon>
          <span>{{ "ORGANISATION.API_KEYS.TITLE" | translate }}</span>
        </a>
        <a routerLink="/impressum" mat-menu-item>
          <mat-icon>assignment</mat-icon>
          <span>{{ "PAGES.IMPRESSUM" | translate }}</span>
        </a>
        <mat-divider></mat-divider>
        <button *ngIf="authService.user$ | async as user" mat-menu-item (click)="logout()">
          <mat-icon>logout</mat-icon>
          <span>{{ "LOGIN.LOGOUT" | translate }}</span>
        </button>
      </mat-menu>
    </span>
  </mat-toolbar>

  <!-- Main Layout with sidenav -->
  <mat-sidenav-container class="flex-1 h-full">
    <!-- Sidebar -->
    <mat-sidenav #sidenav
                 [mode]="isMobile ? 'over' : 'side'"
                 [opened]="!isMobile && !hideSidebar"
                 style="width: 17rem; min-width: 17rem; max-width: 18rem;"
                 class="bg-gray-50 border-r sidebar-sidenav">
      <div class="sidebar-header p-4 font-bold text-lg border-b">
        {{ "NAVIGATION.TITLE" | translate }}
      </div>

      <div class="sidebar-menu">
        <mat-nav-list>
          <a mat-list-item
             routerLink="/audits" routerLinkActive="active-link"
             (click)="isMobile && sidenav.close()">
            <mat-icon matListItemIcon class="!mr-2 !text-inherit">checklist</mat-icon>
            {{ "PAGES.AUDITS" | translate }}
          </a>
          <a mat-list-item *ngIf="userInfoService.isAdminUser()" routerLink="/templates" routerLinkActive="active-link"
             (click)="isMobile && sidenav.close()">
            <mat-icon matListItemIcon class="!mr-2 !text-inherit">description</mat-icon>
            {{ "PAGES.TEMPLATES" | translate }}
          </a>
          <!-- DOCU-751: "Export-Vorlagen" vorerst ausgeblendet (nicht rollout-reif).
               Route/Seite bleiben; Sichtbarkeit über exportTemplatesMenuEnabled,
               später Per-Org-Freischaltung (DOCU-752). -->
          <a mat-list-item *ngIf="userInfoService.isAdminUser() && exportTemplatesMenuEnabled" routerLink="/export-templates" routerLinkActive="active-link"
             (click)="isMobile && sidenav.close()">
            <mat-icon matListItemIcon class="!mr-2 !text-inherit">file_copy</mat-icon>
            {{ "PAGES.EXPORT_TEMPLATES" | translate }}
          </a>
          <a mat-list-item *ngIf="userInfoService.isSystemAdmin()" routerLink="/admin-templates" routerLinkActive="active-link"
             (click)="isMobile && sidenav.close()">
            <mat-icon matListItemIcon class="!mr-2 !text-inherit">admin_panel_settings</mat-icon>
            {{ "PAGES.ADMIN_TEMPLATES" | translate }}
          </a>
          <a mat-list-item *ngIf="userInfoService.isAdminUser()" routerLink="/user" routerLinkActive="active-link"
             (click)="isMobile && sidenav.close()">
            <mat-icon matListItemIcon class="!mr-2 !text-inherit">admin_panel_settings</mat-icon>
            {{ "PAGES.USER_MANAGEMENT" | translate }}
          </a>
          <a mat-list-item routerLink="/organisation" routerLinkActive="active-link"
             (click)="isMobile && sidenav.close()">
            <mat-icon matListItemIcon class="!mr-2 !text-inherit">settings</mat-icon>
            {{ "PAGES.ORGANIZATION" | translate }}
          </a>
        </mat-nav-list>
      </div>

      <div class="sidebar-version" *ngIf="versionInfo">
        <ng-container *ngIf="!isProd()">
          <div>{{ versionInfo.env }}</div>
          <div>API {{ versionInfo.apiCommit || '-' }}</div>
          <div>BackOffice {{ versionInfo.backofficeCommit }}</div>
        </ng-container>

        <ng-container *ngIf="isProd()">
          <div>API {{ versionInfo.apiVersion }}</div>
          <div>BackOffice {{ versionInfo.backofficeVersion }}</div>
        </ng-container>
      </div>
    </mat-sidenav>

    <!-- Help Sidenav (right side) -->
    <mat-sidenav #helpSidenav
                 position="end"
                 mode="over"
                 [opened]="(knowledgePanelService.panelState$ | async)?.isOpen"
                 (closedStart)="knowledgePanelService.close()"
                 style="width: 400px; max-width: 90vw;">
      <app-help-sidenav></app-help-sidenav>
    </mat-sidenav>

    <!-- Main Content -->
    <mat-sidenav-content class="overflow-auto p-6">
      <router-outlet *ngIf="!loading && isBackofficeUser"></router-outlet>

      <div *ngIf="!isLoginPage && !isBackofficeUser" class="flex justify-center items-center min-h-screen bg-gray-50">
        <section class="docuty-card docuty-card--standalone w-full max-w-md">
          <h2 class="text-2xl font-semibold text-center mb-6">{{ "ERROR.NO_RIGHT_TITLE" | translate }}</h2>
          <p class="text-center"> {{ "ERROR.NO_RIGHT_TEXT" | translate }}</p>
        </section>
      </div>
    </mat-sidenav-content>
  </mat-sidenav-container>
</div>

<!-- Login Page fallback -->
<router-outlet *ngIf="isLoginPage"></router-outlet>


